import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, Users, CreditCard, Clock, AlertTriangle, TrendingUp, PawPrint } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE } from "@/lib/config";
import EmailTester from "@/components/admin/EmailTester";

const StatCard = ({ icon: Icon, label, value, hint, accent = "text-[var(--app-text)]" }) => (
  <div className="bg-white border border-[var(--app-border)] rounded-md p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className={`font-display font-extrabold text-3xl ${accent}`}>{value}</div>
    {hint && <div className="text-[11px] text-[var(--app-text-soft)] mt-1 font-mono-tech">{hint}</div>}
  </div>
);

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        const [s, u] = await Promise.all([
          axios.get(`${API_BASE}/admin/stats`),
          axios.get(`${API_BASE}/admin/users`),
        ]);
        setStats(s.data);
        setUsers(u.data.users);
      } catch (e) {
        setErr(e?.response?.data?.detail || e?.message || "Failed to load");
      } finally { setBusy(false); }
    })();
  }, [user]);

  if (user && !user.is_admin) return <Navigate to="/" replace />;
  if (busy) return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--app-text-soft)]" data-testid="admin-loading">Loading dashboard…</div>;
  if (err) return <div className="min-h-screen flex items-center justify-center text-red-600">{err}</div>;

  return (
    <div className="min-h-screen bg-[var(--app-bg)]" data-testid="admin-page">
      <header className="px-6 py-4 border-b border-[var(--app-border)] bg-white flex items-center gap-4">
        <Link to="/" className="text-sm flex items-center gap-1 text-[var(--app-text-soft)] hover:text-[var(--app-text)]" data-testid="admin-back">
          <ChevronLeft className="w-4 h-4" /> Library
        </Link>
        <div className="text-sm font-display font-extrabold tracking-tight">Admin Dashboard</div>
        <div className="ml-auto text-[10px] uppercase tracking-[0.22em] font-display font-bold px-2 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700">Owner</div>
      </header>

      <main className="max-w-6xl mx-auto py-10 px-6 space-y-8">
        {/* Stats grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={CreditCard} label="MRR" value={`$${stats.mrr_usd}`} hint={`$${stats.arr_usd}/yr · ${stats.subscribers} paid`} accent="text-emerald-700" />
          <StatCard icon={Users} label="Total users" value={stats.total_users} hint={`+${stats.new_7d} in 7d · +${stats.new_30d} in 30d`} />
          <StatCard icon={Clock} label="In trial" value={stats.in_trial} hint={`${stats.trial_expired_unpaid} expired unpaid`} accent="text-amber-700" />
          <StatCard icon={TrendingUp} label="Conversion" value={stats.total_users > 0 ? `${Math.round((stats.subscribers / stats.total_users) * 100)}%` : "—"} hint={`${stats.subscribers}/${stats.total_users} subscribed`} />
        </section>

        <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard icon={PawPrint} label="Animals" value={stats.total_animals} />
          <StatCard icon={PawPrint} label="Breedings" value={stats.total_breedings} />
          <StatCard icon={AlertTriangle} label="Admins" value={stats.admins} hint="Free access — no trial" />
        </section>

        {/* User table */}
        <section className="bg-white border border-[var(--app-border)] rounded-md overflow-hidden" data-testid="admin-user-table">
          <div className="px-5 py-3 border-b border-[var(--app-border)] flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">All users · newest first</div>
            <div className="text-xs font-mono-tech text-[var(--app-text-soft)]">{users.length} shown</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--app-bg)] text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">
                <tr>
                  <th className="text-left px-4 py-2 font-display">Email</th>
                  <th className="text-left px-4 py-2 font-display">Name</th>
                  <th className="text-left px-4 py-2 font-display">Status</th>
                  <th className="text-right px-4 py-2 font-display">Animals</th>
                  <th className="text-left px-4 py-2 font-display">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--app-border)]">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                    <td className="px-4 py-2 font-mono-tech text-xs">{u.email}</td>
                    <td className="px-4 py-2">{u.name || <span className="text-[var(--app-text-soft)]">—</span>}</td>
                    <td className="px-4 py-2">
                      {u.is_admin ? (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700">Owner</span>
                      ) : u.is_subscribed ? (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-700">Paying</span>
                      ) : u.trial_days_left > 0 ? (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">{u.trial_days_left}d trial</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-red-300 bg-red-50 text-red-700">Expired</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-tech">{u.animal_count}</td>
                    <td className="px-4 py-2 text-xs font-mono-tech text-[var(--app-text-soft)]">{(u.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Email tester */}
        <EmailTester adminEmail={user?.email} />
      </main>
    </div>
  );
}
